import React, { useCallback, useEffect, useState } from "react";
import { CareComment } from "../../../domain/care_comment/CareComment";
import { CareCommentContent } from "../../../domain/care_comment/CareCommentContent";
import { CareCommentTitle } from "../../../domain/care_comment/CareCommentTitle";
import { useAppDispatch } from "../../../state/store";
import { deleteCareCommentAction, updateCareCommentAction } from "../../../state/usecase/admin/care_comment/actions";
import { useBoolean } from "../../hooks/common/useBoolean";
import { CancelCircleButton } from "../common/button/CancelCircleButton";
import { EditButton } from "../common/button/EditButton";
import { RemoveButton } from "../common/button/RemoveButton";
import { SaveButton } from "../common/button/SaveButton";
import { CareCommentEdit } from "./CareCommentEdit";
import { setQRListFromText, textToLink } from "./linkFunctions";
import { v4 as uuidv4 } from "uuid";

export const CareCommentItem = React.memo(function CareCommentItem(props: CareCommentItemProps) {
    const dispatch = useAppDispatch();

    const { careComment } = props;

    const [isEdit, editOn, editOff, ] = useBoolean();

    const [title, setTitle] = useState(careComment.title.value);

    const [titleError, setTitleError] = useState("");
    
    const [content, setContent] = useState(careComment.content.value);

    const [contentError, setContentError] = useState("");

    const [qrList, setQRList] = useState<string[]>([]);

    useEffect(() => {
        if (careComment) {
            setQRListFromText(careComment.content.value, setQRList);
        }
    }, [careComment]);

    const resetState = useCallback(() => {
        setTitle(careComment.title.value);
        setTitleError("");
        setContent(careComment.content.value);
        setContentError("");
    }, [careComment]);

    const saveClick = useCallback(() => {
        // タイトルの長さチェック
        const titleError = CareCommentTitle.isInvalidSize(title) ? CareCommentTitle.messageInvalidSize("タイトル") : "";
        setTitleError(titleError);

        // 内容の長さチェック
        const contentError = CareCommentContent.isInvalidSize(content) ? CareCommentContent.messageInvalidSize("ケアコメント内容") : "";
        setContentError(contentError);

        if (titleError || contentError) return;

        dispatch(
            updateCareCommentAction({
                requestData: {
                    id: careComment.id,
                    title: CareCommentTitle.fromString(title),
                    content: CareCommentContent.fromString(content)
                }
            })
        );

        editOff();

        resetState();
    }, [title, content, dispatch, careComment, editOff, resetState]);

    const cancelClick = useCallback(() => {
        editOff();

        resetState();
    }, [editOff, resetState]);

    const editClick = useCallback(() => {
        resetState();
        editOn();
    }, [resetState, editOn]);

    const removeClick = useCallback(() => {
        if (!window.confirm("削除してよろしいですか？")) return;

        dispatch(
            deleteCareCommentAction({
                careCommentId: careComment.id
            })
        );
    }, [dispatch, careComment]);

    return (
        <div className={"care_comment_item"}>
            {isEdit ? (
                <>
                    <CareCommentEdit
                        title={title}
                        handleTitle={setTitle}
                        titleError={titleError}
                        content={content}
                        handleContent={setContent}
                        contentError={contentError}
                        show
                    />
                    <div className={"admin_button_area"}>
                        <SaveButton onClick={saveClick} />
                        <CancelCircleButton className={"edit_cancel_button"} onClick={cancelClick} />
                    </div>
                </>
            ) : (
                <>
                    <div className={"title"}>
                        <div>{`${careComment.createdTime.date.japaneseMonthDateFormat()} ${careComment.createdTime.time.formatHourMinute()}`}</div>
                        <div className={"title_text"}>{careComment.title.value}</div>
                    </div>
                    {/* eslint-disable-next-line */} {/* '__html'というプロパティ名がeslintルールに引っかかるためeslintをdisabledにする */}
                    <div className={"content"} dangerouslySetInnerHTML={{ __html: textToLink(careComment.content.value) }}/>
                    <div className={"register_date"}>{`登録日時（${careComment.createdTime.date.japaneseFormat()}）`}</div>
                    <div className={"rq_code"}>
                        {qrList.map(qr => <img key={uuidv4()} src={qr} alt={qr} />)}
                    </div>
                    <div className={"admin_button_area"}>
                        <EditButton onClick={editClick} />
                        <RemoveButton onClick={removeClick} />
                    </div>
                </>
            )}
        </div>
    );
});

type CareCommentItemProps = {
    careComment: CareComment
}
